import jsPDF from "jspdf";
import autoTable, { type RowInput } from "jspdf-autotable";
import type { CashMovementRecord } from "../models/cashMovement";
import fontAsset from "../assets/fonts/NotoSansDevanagari-Regular.ttf";
import type { ShopCashSummary } from "./shopCashCalculations";
import { formatDate } from "./calculations";

interface ShopCashPdfExportParams {
	startDate: string;
	endDate: string;
	cashMovements: CashMovementRecord[];
	summary: ShopCashSummary;
	partnerWithdrawalRows: Array<[string, number]>;
	expenseCategoryRows: Array<[string, number]>;
	totalCashOut: number;
	totalUpiOut: number;
	totalOut: number;
}

const formatCurrency = (value: number) => `Rs. ${value.toFixed(2)}`;
const redAmountColor: [number, number, number] = [211, 47, 47];
const highlightBackgroundColor: [number, number, number] = [255, 249, 196];

const getAmountTextColor = (value: number): [number, number, number] => {
	if (value < 0) {
		return [211, 47, 47];
	}
	if (value > 0) {
		return [46, 125, 50];
	}
	return [33, 33, 33];
};

const formatAmountCell = (
	value: number,
	overrideColor?: [number, number, number],
	highlight?: boolean
) => ({
	content: formatCurrency(value),
	styles: {
		textColor: overrideColor ?? getAmountTextColor(value),
		fontStyle: "bold" as const,
		fillColor: highlight ? highlightBackgroundColor : undefined
	}
});

const buildSummaryRows = (summary: ShopCashSummary): RowInput[] => [
	["Cash Received", formatAmountCell(summary.cashReceived)],
	[
		"Cash Partner Withdrawals",
		formatAmountCell(-summary.partnerCashWithdrawals)
	],
	["Cash Expenses", formatAmountCell(-summary.shopCashExpenses)],
	[
		"Available Cash",
		formatAmountCell(summary.availableCash, undefined, true)
	],
	["UPI Received", formatAmountCell(summary.upiReceived)],
	[
		"UPI Partner Withdrawals",
		formatAmountCell(-summary.partnerUpiWithdrawals)
	],
	["UPI Expenses", formatAmountCell(-summary.shopUpiExpenses)],
	["Available UPI", formatAmountCell(summary.availableUpi, undefined, true)]
];

export const exportShopCashToPdf = ({
	startDate,
	endDate,
	cashMovements,
	summary,
	partnerWithdrawalRows,
	expenseCategoryRows,
	totalCashOut,
	totalUpiOut,
	totalOut
}: ShopCashPdfExportParams) => {
	const doc = new jsPDF({
		orientation: "landscape",
		unit: "pt",
		format: "a4"
	});
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageMargin = 40;

	doc.addFont(fontAsset, "NotoSansDevanagari", "normal");
	doc.setFont("NotoSansDevanagari", "normal");
	doc.setFontSize(16);
	doc.text("Shop Cashbook", pageMargin, 40);

	doc.setFontSize(10);
	doc.text(`Date Range: ${startDate} to ${endDate}`, pageMargin, 60);

	let yPosition = 84;

	autoTable(doc, {
		startY: yPosition,
		head: [["Label", "Value"]],
		body: buildSummaryRows(summary),
		theme: "grid",
		styles: {
			font: "NotoSansDevanagari",
			fontSize: 8,
			cellPadding: 4,
			overflow: "linebreak"
		},
		headStyles: {
			fillColor: [242, 242, 242],
			textColor: [20, 20, 20],
			fontStyle: "bold",
			font: "NotoSansDevanagari"
		},
		margin: { top: 40, right: pageMargin, bottom: 40, left: pageMargin },
		tableWidth: pageWidth - pageMargin * 2
	});
	const summaryTableY =
		(doc as typeof doc & { lastAutoTable?: { finalY: number } })
			.lastAutoTable?.finalY ?? yPosition;
	yPosition = summaryTableY + 20;

	if (partnerWithdrawalRows.length > 0) {
		doc.setFont("NotoSansDevanagari", "normal");
		doc.setFontSize(11);
		doc.text("Partner Withdrawal Totals", pageMargin, yPosition);
		yPosition += 14;
		autoTable(doc, {
			startY: yPosition,
			head: [["Partner", "Total"]],
			body: partnerWithdrawalRows.map(([partnerName, total]) => [
				partnerName,
				formatAmountCell(total, redAmountColor)
			]),
			theme: "grid",
			styles: {
				font: "NotoSansDevanagari",
				fontSize: 8,
				cellPadding: 4,
				overflow: "linebreak"
			},
			headStyles: {
				fillColor: [242, 242, 242],
				textColor: [20, 20, 20],
				fontStyle: "bold",
				font: "NotoSansDevanagari"
			},
			margin: {
				top: 40,
				right: pageMargin,
				bottom: 40,
				left: pageMargin
			},
			tableWidth: pageWidth - pageMargin * 2
		});
		yPosition =
			(doc as typeof doc & { lastAutoTable?: { finalY: number } })
				.lastAutoTable?.finalY ?? yPosition;
		yPosition += 20;
	}

	if (expenseCategoryRows.length > 0) {
		doc.setFont("NotoSansDevanagari", "normal");
		doc.setFontSize(11);
		doc.text("Expense Totals by Category", pageMargin, yPosition);
		yPosition += 14;
		autoTable(doc, {
			startY: yPosition,
			head: [["Category", "Total"]],
			body: expenseCategoryRows.map(([categoryName, total]) => [
				categoryName,
				formatAmountCell(total, redAmountColor)
			]),
			theme: "grid",
			styles: {
				font: "NotoSansDevanagari",
				fontSize: 8,
				cellPadding: 4,
				overflow: "linebreak"
			},
			headStyles: {
				fillColor: [242, 242, 242],
				textColor: [20, 20, 20],
				fontStyle: "bold",
				font: "NotoSansDevanagari"
			},
			margin: {
				top: 40,
				right: pageMargin,
				bottom: 40,
				left: pageMargin
			},
			tableWidth: pageWidth - pageMargin * 2
		});
		yPosition =
			(doc as typeof doc & { lastAutoTable?: { finalY: number } })
				.lastAutoTable?.finalY ?? yPosition;
		yPosition += 20;
	}

	doc.setFont("NotoSansDevanagari", "normal");
	doc.setFontSize(11);
	doc.text("Movement Details", pageMargin, yPosition);
	yPosition += 14;

	autoTable(doc, {
		startY: yPosition,
		head: [
			[
				"Date",
				"Type",
				"Category/Person",
				"Vendor",
				"Description",
				"Source",
				"Amount",
				"Entered By"
			]
		],
		body: cashMovements.map((movement) => [
			formatDate(movement.movementDate),
			movement.type === "EXPENSE" ? "Expense" : "Partner Withdrawal",
			movement.type === "EXPENSE"
				? (movement.expenseCategory ?? "")
				: (movement.personName ?? ""),
			movement.vendorName || "-",
			movement.description,
			movement.paymentSource,
			formatAmountCell(-movement.amount),
			movement.username
		]),
		theme: "grid",
		styles: {
			font: "NotoSansDevanagari",
			fontSize: 7,
			cellPadding: 3,
			overflow: "linebreak"
		},
		headStyles: {
			fillColor: [242, 242, 242],
			textColor: [20, 20, 20],
			fontStyle: "bold",
			font: "NotoSansDevanagari"
		},
		margin: { top: 40, right: pageMargin, bottom: 40, left: pageMargin },
		tableWidth: pageWidth - pageMargin * 2
	});

	const lastAutoTableY =
		(doc as typeof doc & { lastAutoTable?: { finalY: number } })
			.lastAutoTable?.finalY ?? yPosition;

	autoTable(doc, {
		startY: lastAutoTableY + 16,
		head: [["", ""]],
		body: [
			["Total Cash Out", formatAmountCell(-totalCashOut)],
			["Total UPI Out", formatAmountCell(-totalUpiOut)],
			["Total Out", formatAmountCell(-totalOut)]
		],
		theme: "grid",
		styles: {
			font: "NotoSansDevanagari",
			fontSize: 8,
			cellPadding: 4,
			overflow: "linebreak"
		},
		headStyles: {
			fillColor: [242, 242, 242],
			textColor: [20, 20, 20],
			fontStyle: "bold",
			font: "NotoSansDevanagari"
		},
		margin: { top: 40, right: pageMargin, bottom: 40, left: pageMargin },
		tableWidth: pageWidth - pageMargin * 2,
		columnStyles: {
			0: { cellWidth: 200 },
			1: { halign: "right" }
		}
	});

	const timestamp = new Date().toISOString().slice(0, 10);
	doc.save(`shop-cash_${timestamp}.pdf`);
};
